/**
 * API Routes - Single Source of Truth (SSOT)
 *
 * Centralized definition of all REST API routes.
 * This file is the ONLY place where API URLs should be defined.
 *
 * IMPORTANT FOR AI ASSISTANTS:
 * When adding new API methods to api.ts or auth-api.ts:
 * 1. FIRST add the route definition here
 * 2. THEN import and use it in the API client
 * 3. NEVER hardcode URL strings directly in api.ts
 *
 * Route naming convention:
 * - list: GET collection (e.g., /protocols)
 * - detail: GET single item (e.g., /protocols/{id})
 * - create: POST to collection (same as list URL)
 * - update: PUT to single item (same as detail URL)
 * - delete: DELETE single item (same as detail URL)
 *
 * @see src/API/Endpoints/ for PHP implementation
 * @see CLAUDE.md for documentation
 */

import type { TagEntityType } from '@/types/tag';

// ============================================================================
// PROTOCOLS
// @see src/API/Endpoints/ProtocolsEndpoint.php
// ============================================================================

export const PROTOCOL_ROUTES = {
	/** GET /protocols - list, POST /protocols - create */
	list: '/protocols',
	/** GET /protocols/{id} */
	detail: (id: number) => `/protocols/${id}`,
	/** GET /protocols/{id}/email-options */
	emailOptions: (id: number) => `/protocols/${id}/email-options`,
	/** POST /protocols/{id}/resend-email */
	resendEmail: (id: number) => `/protocols/${id}/resend-email`,
} as const;

// ============================================================================
// CLIENTS
// @see src/API/Endpoints/ClientsEndpoint.php
// ============================================================================

export const CLIENT_ROUTES = {
	/** GET /clients - list, POST /clients - create */
	list: '/clients',
	/** GET /clients/{id}, PUT /clients/{id} */
	detail: (id: number) => `/clients/${id}`,
	/** GET /clients/nearby?lat=X&lng=Y - find by GPS */
	nearby: '/clients/nearby',

	// Client Portal endpoints
	/** GET /clients/{id}/user-status - get portal user status */
	userStatus: (id: number) => `/clients/${id}/user-status`,
	/** POST /clients/{id}/invite - invite client to portal */
	invite: (id: number) => `/clients/${id}/invite`,
	/** POST /clients/{id}/assign-user - assign existing WP user */
	assignUser: (id: number) => `/clients/${id}/assign-user`,
	/** DELETE /clients/{id}/user - disconnect user from client (M:N - optional user_id param) */
	disconnectUser: (id: number, userId?: number) =>
		userId ? `/clients/${id}/user?user_id=${userId}` : `/clients/${id}/user`,
} as const;

// ============================================================================
// CLIENT ADDRESSES
// @see src/API/Endpoints/ClientAddressesEndpoint.php
// ============================================================================

export const CLIENT_ADDRESS_ROUTES = {
	/** GET /clients/{clientId}/addresses, POST - create */
	list: (clientId: number) => `/clients/${clientId}/addresses`,
	/** GET, PUT, DELETE /clients/{clientId}/addresses/{id} */
	detail: (clientId: number, addressId: number) => `/clients/${clientId}/addresses/${addressId}`,
	/** PUT /clients/{clientId}/addresses/{id}/set-default */
	setDefault: (clientId: number, addressId: number) =>
		`/clients/${clientId}/addresses/${addressId}/set-default`,
} as const;

// ============================================================================
// FORM DATA (Read-only, active items only)
// @see src/API/Endpoints/MiscEndpoint.php
//
// These return ONLY ACTIVE items for use in ProtocolForm dropdowns.
// For admin CRUD operations, use CODEBOOK_ROUTES instead.
// ============================================================================

export const FORM_DATA_ROUTES = {
	/** GET /materials - active materials for ProtocolForm */
	materials: '/materials',
	/** GET /installation-types - active installation types for ProtocolForm */
	installationTypes: '/installation-types',
	/** GET /pests - active pests for ProtocolForm */
	pests: '/pests',
} as const;

// ============================================================================
// PDF
// @see src/API/Endpoints/MiscEndpoint.php
// ============================================================================

export const PDF_ROUTES = {
	/** POST /pdf/preview - generate PDF preview (developer mode) */
	preview: '/pdf/preview',
} as const;

// ============================================================================
// PHOTOS
// @see src/API/Endpoints/PhotosEndpoint.php
// ============================================================================

export const PHOTO_ROUTES = {
	/** POST /photos/upload */
	upload: '/photos/upload',
	/** POST /photos/finalize */
	finalize: '/photos/finalize',
	/** DELETE /photos/{filename} */
	delete: (filename: string) => `/photos/${encodeURIComponent(filename)}`,
} as const;

// ============================================================================
// CODEBOOKS (Admin CRUD - includes inactive items)
// @see src/API/Endpoints/CodebooksEndpoint.php
//
// ⚠️ IMPORTANT: ALL codebook operations use /codebooks/{type} prefix!
// There are NO separate endpoints like /work-types or /work-subtypes.
//
// For read-only access (active items only), use FORM_DATA_ROUTES.
// ============================================================================

/** Valid codebook types */
export type CodebookType =
	| 'materials'
	| 'installation-types'
	| 'pests'
	| 'work-types'
	| 'work-subtypes'
	| 'recommendations';

export const CODEBOOK_ROUTES = {
	/** GET /codebooks/{type} - list all, POST - create */
	list: (type: CodebookType) => `/codebooks/${type}`,
	/** GET, PUT, DELETE /codebooks/{type}/{id} */
	detail: (type: CodebookType, id: number) => `/codebooks/${type}/${id}`,
	/** POST /codebooks/{type}/{id}/toggle - toggle is_active */
	toggle: (type: CodebookType, id: number) => `/codebooks/${type}/${id}/toggle`,
	/** POST /codebooks/{type}/reorder - bulk reorder items */
	reorder: (type: CodebookType) => `/codebooks/${type}/reorder`,
} as const;

// ============================================================================
// RECOMMENDATIONS
// @see src/API/Endpoints/RecommendationsEndpoint.php
// ============================================================================

export const RECOMMENDATION_ROUTES = {
	/** GET /recommendations/filtered - filtered for ProtocolForm */
	filtered: '/recommendations/filtered',
	/** POST /recommendations/upload-image */
	uploadImage: '/recommendations/upload-image',
	/** DELETE /recommendations/image/{filename} */
	deleteImage: (filename: string) => `/recommendations/image/${encodeURIComponent(filename)}`,
} as const;

// ============================================================================
// USERS
// @see src/API/Endpoints/UsersEndpoint.php
// ============================================================================

export const USER_ROUTES = {
	/** GET /users - list, POST /users - create */
	list: '/users',
	/** GET, PUT, DELETE /users/{id} */
	detail: (id: number) => `/users/${id}`,
	/** POST /users/{id}/reset-password */
	resetPassword: (id: number) => `/users/${id}/reset-password`,
	/** POST /user/locale - set current user's locale preference */
	setLocale: '/user/locale',
} as const;

// ============================================================================
// CLIENT USERS (ddd_client role management from UsersPage)
// @see src/API/Endpoints/ClientUserEndpoint.php
// ============================================================================

export const CLIENT_USER_ROUTES = {
	/** POST /client-users - create client user with multiple clients */
	create: '/client-users',
	/** PUT /client-users/{id} - update client user (name + clients) */
	update: (id: number) => `/client-users/${id}`,
	/** DELETE /client-users/{id} - disconnect from all clients + cleanup */
	delete: (id: number) => `/client-users/${id}`,
} as const;

// ============================================================================
// ACTIVITY LOGS
// @see src/API/Endpoints/ActivityLogsEndpoint.php
// ============================================================================

export const ACTIVITY_LOG_ROUTES = {
	/** GET /activity-logs */
	list: '/activity-logs',
	/** GET /activity-logs/{id} */
	detail: (id: number) => `/activity-logs/${id}`,
	/** GET /activity-logs/filters */
	filters: '/activity-logs/filters',
} as const;

// ============================================================================
// SYNC LOG
// @see src/API/Endpoints/SyncLogEndpoint.php
// ============================================================================

export const SYNC_LOG_ROUTES = {
	/** POST /sync-log */
	log: '/sync-log',
} as const;

// ============================================================================
// SYSTEM
// @see src/API/Endpoints/SystemEndpoint.php
// ============================================================================

export const SYSTEM_ROUTES = {
	/** GET /system/version - get plugin version (public, for update check) */
	version: '/system/version',
	/** GET /system/check-update - check for available updates */
	checkUpdate: '/system/check-update',
	/** POST /system/run-update - execute plugin update */
	runUpdate: '/system/run-update',
	/** POST /system/run-migration - manually run database migrations */
	runMigration: '/system/run-migration',
} as const;

// ============================================================================
// AUTH
// @see src/API/Endpoints/AuthEndpoint.php
// ============================================================================

export const AUTH_ROUTES = {
	/** POST /auth/login */
	login: '/auth/login',
	/** POST /auth/logout */
	logout: '/auth/logout',
	/** GET /auth/check */
	check: '/auth/check',
	/** POST /auth/forgot-password */
	forgotPassword: '/auth/forgot-password',
	/** POST /auth/validate-reset-key */
	validateResetKey: '/auth/validate-reset-key',
	/** POST /auth/set-password */
	setPassword: '/auth/set-password',
} as const;

// ============================================================================
// SETTINGS
// @see src/API/Endpoints/SettingsEndpoint.php
// ============================================================================

export const SETTINGS_ROUTES = {
	/** GET /settings - get all settings */
	get: '/settings',
	/** POST /settings - update settings */
	update: '/settings',
	/** GET /settings/schema - get settings schema */
	schema: '/settings/schema',
	/** POST /settings/upload-image - upload image for settings */
	uploadImage: '/settings/upload-image',
} as const;

// ============================================================================
// TAGS
// @see src/API/Endpoints/TagsEndpoint.php
// ============================================================================

export const TAG_ROUTES = {
	/** GET /tags?entity_type=X - list tags, POST /tags - create */
	list: '/tags',
	/** GET, PUT, DELETE /tags/{id} */
	detail: (id: number) => `/tags/${id}`,
	/** POST /tags/{id}/toggle - toggle is_active */
	toggle: (id: number) => `/tags/${id}/toggle`,
	/** POST /tags/reorder - bulk reorder tags */
	reorder: '/tags/reorder',
} as const;

// ============================================================================
// ENTITY TAGS
// @see src/API/Endpoints/EntityTagsEndpoint.php
// ============================================================================

export const ENTITY_TAG_ROUTES = {
	/** GET /entity-tags/{entityType}/{entityId} - get tags for entity */
	list: (entityType: TagEntityType, entityId: number) => `/entity-tags/${entityType}/${entityId}`,
	/** POST /entity-tags/{entityType}/{entityId} - assign tag (body: { tag_id }) */
	assign: (entityType: TagEntityType, entityId: number) =>
		`/entity-tags/${entityType}/${entityId}`,
	/** PUT /entity-tags/{entityType}/{entityId} - bulk update (body: { tag_ids: [] }) */
	bulkUpdate: (entityType: TagEntityType, entityId: number) =>
		`/entity-tags/${entityType}/${entityId}`,
	/** DELETE /entity-tags/{entityType}/{entityId}/{tagId} - unassign tag */
	unassign: (entityType: TagEntityType, entityId: number, tagId: number) =>
		`/entity-tags/${entityType}/${entityId}/${tagId}`,
} as const;

// ============================================================================
// PERMISSIONS
// @see src/API/Endpoints/PermissionsEndpoint.php
// ============================================================================

export const PERMISSIONS_ROUTES = {
	/** GET /permissions - get all roles with capabilities */
	get: '/permissions',
	/** POST /permissions - update role capabilities */
	update: '/permissions',
	/** POST /permissions/reset - reset to defaults */
	reset: '/permissions/reset',
} as const;

// ============================================================================
// INTEGRATIONS
// @see src/API/Endpoints/IntegrationsEndpoint.php
// ============================================================================

export const INTEGRATION_ROUTES = {
	/** GET /integrations - list available connectors */
	list: '/integrations',
	/** POST /integrations/{connector}/prepare - prepare import (fetch all, consolidate, store in staging) */
	prepare: (connector: string) => `/integrations/${connector}/prepare`,
	/** GET /integrations/{connector}/preview?session_id=xxx - get paginated preview from staging */
	preview: (connector: string) => `/integrations/${connector}/preview`,
	/** POST /integrations/{connector}/import - execute import from staging */
	import: (connector: string) => `/integrations/${connector}/import`,
	/** POST /integrations/{connector}/test - test connection */
	test: (connector: string) => `/integrations/${connector}/test`,
	/** DELETE /integrations/session/{session_id} - cleanup session */
	deleteSession: (sessionId: string) => `/integrations/session/${sessionId}`,
} as const;

// ============================================================================
// ROLES
// @see src/API/Endpoints/RolesEndpoint.php
// ============================================================================

export const ROLE_ROUTES = {
	/** GET /roles - list all available DDD roles */
	list: '/roles',
} as const;

// ============================================================================
// STATS
// @see src/API/Endpoints/StatsEndpoint.php
// ============================================================================

export const STATS_ROUTES = {
	/** GET /stats/summary - main dashboard summary (protocols, clients, technicians) */
	summary: '/stats/summary',
	/** GET /stats/protocols-over-time - protocols count by date for bar chart */
	protocolsOverTime: '/stats/protocols-over-time',
	/** GET /stats/work-types - work types distribution for pie chart */
	workTypes: '/stats/work-types',
	/** GET /stats/top-clients - top 5 clients by protocol count */
	topClients: '/stats/top-clients',
	/** GET /stats/technicians - technician performance */
	technicians: '/stats/technicians',
} as const;

// ============================================================================
// AGGREGATED EXPORT
// ============================================================================

/**
 * All API routes grouped by domain
 *
 * Usage:
 * ```typescript
 * import { API_ROUTES } from '@/lib/api-routes';
 *
 * // Static routes
 * this.request(API_ROUTES.protocols.list);
 *
 * // Dynamic routes
 * this.request(API_ROUTES.protocols.detail(123));
 * this.request(API_ROUTES.codebooks.list('work-types'));
 * ```
 */
export const API_ROUTES = {
	protocols: PROTOCOL_ROUTES,
	clients: CLIENT_ROUTES,
	clientAddresses: CLIENT_ADDRESS_ROUTES,
	formData: FORM_DATA_ROUTES,
	pdf: PDF_ROUTES,
	photos: PHOTO_ROUTES,
	codebooks: CODEBOOK_ROUTES,
	recommendations: RECOMMENDATION_ROUTES,
	users: USER_ROUTES,
	clientUsers: CLIENT_USER_ROUTES,
	activityLogs: ACTIVITY_LOG_ROUTES,
	syncLog: SYNC_LOG_ROUTES,
	system: SYSTEM_ROUTES,
	auth: AUTH_ROUTES,
	settings: SETTINGS_ROUTES,
	tags: TAG_ROUTES,
	entityTags: ENTITY_TAG_ROUTES,
	permissions: PERMISSIONS_ROUTES,
	integrations: INTEGRATION_ROUTES,
	roles: ROLE_ROUTES,
	stats: STATS_ROUTES,
} as const;
